import axios from 'axios'

import axiosInstance from 'api/axiosInstance'
import { type AudioAttachmentResponse } from 'types/api/attachment'

export const normalizeMimeType = (contentType?: string | null): string => {
  if (!contentType) return ''
  return contentType.toLowerCase().split(';')[0].trim()
}

export const getVideoThumbs = async (videos: string[]) =>
  await axiosInstance({ method: 'GET', url: '/api/attachment/video/thumbs', params: { videos } })

export const attachAudio = async (
  data: FormData,
  setProgress?: (loaded: number, total: number, cancelCb: () => void) => void,
  signal?: AbortSignal
) => {
  const controller = !signal ? new AbortController() : null
  const finalSignal = signal ?? controller?.signal

  try {
    return await axiosInstance<AudioAttachmentResponse>({
      method: 'POST',
      url: '/api/attachment/sound',
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: finalSignal,
      onUploadProgress: p => setProgress?.(p.loaded, p.total as number, () => controller?.abort())
    })
  } catch (error: any) {
    throw new Error(error?.message || 'Audio upload failed')
  }
}

export const confirmVerificationPhotoUpload = async (
  payload: {
    s3_key: string
    kind: 'id_photo' | 'id_hold_photo'
    original_name?: string
    mime_type?: string
    size?: number
  },
  signal?: AbortSignal
) => {
  try {
    return await axiosInstance.post(
      '/api/attachment/verification/photo/from-s3',
      { ...payload, mime_type: payload.mime_type ? normalizeMimeType(payload.mime_type) : payload.mime_type },
      { signal }
    )
  } catch (error: any) {
    throw new Error(error?.message || 'Verification photo confirm failed')
  }
}

export const presignPhoto = async (
  params: {
    type: 'avatar' | 'cover' | 'post_image' | 'story_image' | 'verification_id' | 'verification_id_hold' | 'preview_cover'
    contentType: string
    size: number
    key?: string
  },
  signal?: AbortSignal
) => {
  try {
    const { data } = await axiosInstance.post<{ url: string; key: string; contentType?: string }>(
      '/api/attachment/presign',
      { ...params, contentType: normalizeMimeType(params.contentType) },
      { signal }
    )
    return data
  } catch (error: any) {
    throw new Error(error?.message || 'Presign failed')
  }
}

export const uploadFileToS3 = async (
  putUrl: string,
  file: File,
  setProgress?: (loaded: number, total: number, cancelCb: () => void) => void,
  signal?: AbortSignal,
  contentType?: string
) => {
  const controller = !signal ? new AbortController() : null
  const finalSignal = signal ?? controller?.signal

  try {
    await axios.put(putUrl, file, {
      headers: { 'Content-Type': normalizeMimeType(contentType || file.type) },
      signal: finalSignal,
      onUploadProgress: p => setProgress?.(p.loaded, (p.total as number) ?? file.size, () => controller?.abort())
    })
    return true
  } catch (error: any) {
    throw new Error(error?.message || 'S3 upload failed')
  }
}

export const VIDEO_MULTIPART_PART_SIZE = 5 * 1024 * 1024
export const VIDEO_MULTIPART_CONCURRENCY = 4

export type VideoMultipartType = 'post_video' | 'story_video'

export interface MultipartPresignedPart {
  partNumber: number
  url: string
}

export interface StartVideoMultipartResponse {
  status: 'OK'
  key: string
  uploadId: string
  partSize: number
  totalParts: number
  parts: MultipartPresignedPart[]
  type: VideoMultipartType
  contentType: string
}

export interface CompletedMultipartPart {
  PartNumber: number
  ETag: string
}

export const startVideoMultipart = async (
  payload: { type: VideoMultipartType; contentType: string; size: number },
  signal?: AbortSignal
): Promise<StartVideoMultipartResponse> => {
  const response = await axiosInstance({
    method: 'POST',
    url: '/api/attachment/video/multipart/start',
    data: payload,
    signal
  })

  return (response?.data?.data ?? response?.data ?? response) as StartVideoMultipartResponse
}

export const completeVideoMultipart = async (
  params: { key: string; uploadId: string; parts: CompletedMultipartPart[] },
  signal?: AbortSignal
) => {
  try {
    const { data } = await axiosInstance.post<{
      status: 'OK'
      key: string
      etag?: string | null
      location?: string | null
    }>('/api/attachment/video/multipart/complete', params, { signal })
    return data
  } catch (error: any) {
    throw new Error(error?.response?.data?.error || error?.message || 'Unable to complete multipart upload')
  }
}

export const abortVideoMultipart = async (
  params: { key: string; uploadId: string },
  signal?: AbortSignal
) => {
  try {
    const { data } = await axiosInstance.post<{ status: 'OK' }>(
      '/api/attachment/video/multipart/abort',
      params,
      { signal }
    )
    return data
  } catch (error: any) {
    console.error('[multipart] abort failed', error)
    return null
  }
}

const VIDEO_MULTIPART_MAX_ATTEMPTS = 8
const VIDEO_MULTIPART_MAX_RETRY_DELAY = 15000

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'))
      return
    }

    const timer = window.setTimeout(() => {
      cleanup()
      resolve()
    }, ms)

    const onAbort = () => {
      window.clearTimeout(timer)
      cleanup()
      reject(new DOMException('Upload aborted', 'AbortError'))
    }

    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort)
    }

    signal?.addEventListener('abort', onAbort, { once: true })
  })

const waitUntilOnline = (signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (navigator.onLine) {
      resolve()
      return
    }

    const onOnline = () => {
      cleanup()
      resolve()
    }

    const onAbort = () => {
      cleanup()
      reject(new DOMException('Upload aborted', 'AbortError'))
    }

    const cleanup = () => {
      window.removeEventListener('online', onOnline)
      signal?.removeEventListener('abort', onAbort)
    }

    window.addEventListener('online', onOnline, { once: true })
    signal?.addEventListener('abort', onAbort, { once: true })
  })

const isAbortError = (error: any) =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

const isRetryableUploadError = (error: any) => {
  if (isAbortError(error)) return false

  const status = error?.response?.status

  // No HTTP response means the request failed at the
  // transport/browser level: network changed, Wi-Fi loss,
  // DNS/socket failure, etc.
  if (!status) return true

  return (
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status >= 500
  )
}

const getMultipartRetryDelay = (attempt: number) => {
  const exponential = Math.min(
    1000 * (2 ** (attempt - 1)),
    VIDEO_MULTIPART_MAX_RETRY_DELAY
  )

  return exponential + Math.floor(Math.random() * 500)
}


export const uploadVideoMultipart = async ({
  file,
  partSize,
  totalParts,
  parts,
  concurrency = VIDEO_MULTIPART_CONCURRENCY,
  signal,
  setProgress
}: {
  file: File
  key: string
  uploadId: string
  partSize: number
  totalParts: number
  parts: MultipartPresignedPart[]
  contentType?: string
  concurrency?: number
  signal?: AbortSignal
  setProgress?: (loaded: number, total: number) => void
}): Promise<CompletedMultipartPart[]> => {
  if (!Array.isArray(parts) || parts.length !== totalParts) {
    throw new Error(
      `Expected ${totalParts} multipart URLs, received ${parts?.length ?? 0}.`
    )
  }

  const partUrlMap = new Map<number, string>()

  for (const part of parts) {
    const partNumber = Number(part.partNumber)

    if (
      !Number.isInteger(partNumber) ||
      partNumber < 1 ||
      partNumber > totalParts ||
      typeof part.url !== 'string' ||
      !part.url
    ) {
      throw new Error(
        'Invalid multipart part URL returned by setup endpoint.'
      )
    }

    partUrlMap.set(partNumber, part.url)
  }

  if (partUrlMap.size !== totalParts) {
    throw new Error(
      'Multipart setup response contained missing or duplicate part URLs.'
    )
  }

  const uploadedBytesByPart = new Map<number, number>()
  const completedParts: CompletedMultipartPart[] = []
  let nextPartNumber = 1

  const reportProgress = (
    partNumber: number,
    loaded: number
  ) => {
    uploadedBytesByPart.set(partNumber, loaded)

    let totalLoaded = 0

    uploadedBytesByPart.forEach(value => {
      totalLoaded += value
    })

    setProgress?.(
      Math.min(totalLoaded, file.size),
      file.size
    )
  }

  const throwIfAborted = () => {
    if (signal?.aborted) {
      throw new DOMException(
        'Upload aborted',
        'AbortError'
      )
    }
  }

  const uploadPart = async (
    partNumber: number,
    putUrl: string,
    blob: Blob
  ): Promise<CompletedMultipartPart> => {
    let lastError: any = null

    for (
      let attempt = 1;
      attempt <= VIDEO_MULTIPART_MAX_ATTEMPTS;
      attempt++
    ) {
      throwIfAborted()

      // Restart this part's progress from zero.
      reportProgress(partNumber, 0)

      try {
        const response = await axios.put(
          putUrl,
          blob,
          {
            signal,
            onUploadProgress: event => {
              reportProgress(
                partNumber,
                Math.min(
                  event.loaded ?? 0,
                  blob.size
                )
              )
            }
          }
        )

        throwIfAborted()

        const rawETag =
          response.headers?.etag ??
          response.headers?.ETag

        if (
          typeof rawETag !== 'string' ||
          !rawETag
        ) {
          throw new Error(
            `S3 did not return an ETag for multipart part ${partNumber}.`
          )
        }

        reportProgress(
          partNumber,
          blob.size
        )

        if (attempt > 1) {
          console.info(
            '[multipart] part recovered',
            {
              partNumber,
              attempt
            }
          )
        }

        return {
          PartNumber: partNumber,
          ETag: rawETag
        }
      } catch (error: any) {
        lastError = error

        if (
          signal?.aborted ||
          isAbortError(error)
        ) {
          throw error
        }

        if (
          !isRetryableUploadError(error) ||
          attempt >= VIDEO_MULTIPART_MAX_ATTEMPTS
        ) {
          throw error
        }

        console.warn(
          '[multipart] part upload failed, retrying',
          {
            partNumber,
            attempt,
            nextAttempt: attempt + 1,
            code: error?.code ?? null,
            message: error?.message ?? null,
            status:
              error?.response?.status ?? null,
            online: navigator.onLine
          }
        )

        // Don't burn attempts while the browser
        // knows it is offline.
        if (!navigator.onLine) {
          console.warn(
            '[multipart] waiting for network',
            { partNumber }
          )

          await waitUntilOnline(signal)

          console.info(
            '[multipart] network restored',
            { partNumber }
          )
        }

        const delay =
          getMultipartRetryDelay(attempt)

        await sleep(delay, signal)
      }
    }

    throw (
      lastError ??
      new Error(
        `Unable to upload multipart part ${partNumber}.`
      )
    )
  }

  const worker = async () => {
    while (true) {
      throwIfAborted()

      const partNumber =
        nextPartNumber++

      if (partNumber > totalParts) {
        return
      }

      const putUrl =
        partUrlMap.get(partNumber)

      if (!putUrl) {
        throw new Error(
          `Missing presigned URL for part ${partNumber}.`
        )
      }

      const start =
        (partNumber - 1) *
        partSize

      const end =
        Math.min(
          start + partSize,
          file.size
        )

      const blob =
        file.slice(start, end)

      uploadedBytesByPart.set(
        partNumber,
        0
      )

      const completed =
        await uploadPart(
          partNumber,
          putUrl,
          blob
        )

      completedParts.push(
        completed
      )
    }
  }

  const workerCount =
    Math.max(
      1,
      Math.min(
        concurrency,
        totalParts
      )
    )

  await Promise.all(
    Array.from(
      { length: workerCount },
      () => worker()
    )
  )

  throwIfAborted()

  completedParts.sort(
    (a, b) =>
      a.PartNumber -
      b.PartNumber
  )

  if (
    completedParts.length !==
    totalParts
  ) {
    throw new Error(
      `Multipart upload completed ${completedParts.length}/${totalParts} parts.`
    )
  }

  setProgress?.(
    file.size,
    file.size
  )

  return completedParts
}

export const attachPhotoFromS3 = async (
  payload: {
    s3_key: string
    type?: string
    group_id?: string
    recipients?: string[]
    hash_image?: string
  },
  signal?: AbortSignal
) => {
  try {
    return await axiosInstance.post(
      '/api/attachment/photo/from-s3',
      { ...payload, type: payload.type ?? 'post' },
      { signal }
    )
  } catch (error: any) {
    throw new Error(error?.message || 'Attach-photo-from-S3 failed')
  }
}

export const attachStoryPhotoFromS3 = async (
  payload: { s3_key: string; hash_image: string; group_id?: string; recipients?: string[] },
  signal?: AbortSignal
) => {
  try {
    return await axiosInstance.post('/api/attachment/photo/from-s3', { ...payload, type: 'story' }, { signal })
  } catch (error: any) {
    throw new Error(error?.message || 'Attach-story-photo-from-S3 failed')
  }
}

export const attachVideoFromS3 = async (
  payload: {
    s3_key: string
    duration?: number | null
    orientation?: 'Landscape' | 'Portrait' | null
    video_hash?: string | null
    preview?: boolean
    is_preview_video?: boolean
    type?: 'post' | 'message' | 'story'
  },
  signal?: AbortSignal
) => {
  try {
    const res = await axiosInstance.post(
      '/api/attachment/video/from-s3',
      { ...payload, type: payload.type ?? 'post' },
      { signal }
    )
    return res.data
  } catch (error: any) {
    throw new Error(error?.message || 'Attach-from-S3 failed')
  }
}

export const attachPreviewCoverFromS3 = async (
  data: {
    video_id: string
    s3_key: string
    expected_bytes?: number
  },
  signal?: AbortSignal
) =>
  await axiosInstance.post(
    '/api/attachment/preview-cover/from-s3',
    data,
    { signal }
  )

export const attachStoryVideoFromS3 = async (
  payload: {
    s3_key: string
    duration?: number | null
    orientation?: 'Landscape' | 'Portrait' | null
    video_hash?: string | null
    post_id?: string
  },
  signal?: AbortSignal
) => {
  try {
    const res = await axiosInstance.post('/api/attachment/video/from-s3', { ...payload, type: 'story' }, { signal })
    return res.data
  } catch (error: any) {
    throw new Error(error?.message || 'Attach-story-from-S3 failed')
  }
}

export type PhotoDupeCheckResponse = {
  status: 'OK'
  data: {
    id: string
    path: string
    thumb_path: string | null
    storage_path: string
    original_orientation?: string | null
    image_hash: string
    exif_media?: any | null
    post?: any
  }
}

export type VideoDupeCheckResponse = {
  status: 'OK'
  data: {
    id?: string
    video_id?: string
    path: string
    thumb_path: string | null
    storage_path: string
    orientation?: string | null
    original_orientation?: string | null
    video_hash: string
    duration?: number | null
    exif_media?: {
      entity_id: string | null
      entity_type?: string | null
      updated_at: string | null
      viewed?: number | null
      group_ids?: string[]
    } | null
    post?: any
    thumbnails?: string[]
    active_thumbnail?: number | null
    active_thumb?: string | null
    custom_thumb_key?: string | null
    preview_video_id?: string | null
    custom_preview_url?: string | null
  }
}

type DupeCheckParams = {
  h: string
  size?: number
  group_ids?: string[]
  recipients?: string[]
}

export const checkPhotoDupe = async (params: DupeCheckParams, signal?: AbortSignal) => {
  try {
    const res = await axiosInstance.get<PhotoDupeCheckResponse>('/api/attachment/photo/dupe', { params, signal })
    return res.data
  } catch (error: any) {
    if (error?.response?.status === 404) return null
    throw new Error(error?.message || 'Photo dupe check failed')
  }
}

export const checkVideoDupe = async (params: DupeCheckParams, signal?: AbortSignal) => {
  try {
    const res = await axiosInstance.get<VideoDupeCheckResponse>('/api/attachment/video/dupe', { params, signal })
    return res.data
  } catch (error: any) {
    if (error?.response?.status === 404) return null
    throw new Error(error?.message || 'Video dupe check failed')
  }
}
